// AbsenceRequestPage - Candidate form + Admin/Trainer review dashboard
// Supports: create, edit, delete, review (approve/reject/complement)
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileX, Loader2, Plus, X, Upload, CheckCircle2, XCircle,
  AlertCircle, Download, Eye, Clock, Send, Pencil, Trash2
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http:/, 'https:');

function api(path, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(`${BACKEND}/api${path}`, {
    ...opts,
    headers: {
      ...(opts.formData ? {} : { "Content-Type": "application/json" }),
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
}

const STATUS_CONFIG = {
  pending:           { label: "En attente",       color: "#f59e0b", bg: "rgba(245,158,11,0.1)", icon: Clock },
  approved:          { label: "Approuvée",        color: "#16a34a", bg: "rgba(22,163,74,0.1)",  icon: CheckCircle2 },
  rejected:          { label: "Refusée",          color: "#dc2626", bg: "rgba(220,38,38,0.1)",  icon: XCircle },
  complement_needed: { label: "Complément requis", color: "#6366f1", bg: "rgba(99,102,241,0.1)", icon: AlertCircle },
};

export default function AbsenceRequestPage() {
  const { workspaceId } = useParams();
  const { canManage, user } = useAuth();
  const isCandidate = user?.role === "candidate" || user?.role === "student";

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reviewId, setReviewId] = useState(null);
  const [reviewComment, setReviewComment] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [editingRequest, setEditingRequest] = useState(null); // request being edited
  const [deleting, setDeleting] = useState(null);

  // Form state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null);

  useEffect(() => { loadData(); }, [workspaceId, filterStatus]);

  const loadData = async () => {
    setLoading(true);
    try {
      const url = `/attendance/absence-requests?workspace_id=${workspaceId}${filterStatus ? `&status=${filterStatus}` : ""}`;
      const res = await api(url);
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests || []);
      }
    } catch (err) {
      console.error("Error:", err);
    }
    setLoading(false);
  };

  const resetForm = () => {
    setStartDate(""); setEndDate(""); setReason(""); setFile(null);
    setEditingRequest(null); setShowForm(false);
  };

  const openEditForm = (r) => {
    setEditingRequest(r);
    setStartDate(r.start_date?.split("T")[0] || "");
    setEndDate(r.end_date?.split("T")[0] || "");
    setReason(r.reason || "");
    setFile(null);
    setShowForm(true);
  };

  const openNewForm = () => {
    resetForm();
    setShowForm(true);
  };

  const submitRequest = async () => {
    if (!startDate || !endDate || !reason.trim()) {
      return toast.error("Veuillez remplir tous les champs obligatoires");
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("workspace_id", workspaceId);
      formData.append("start_date", startDate);
      formData.append("end_date", endDate);
      formData.append("reason", reason);
      if (file) formData.append("file", file);

      const token = localStorage.getItem("token");
      const isEdit = !!editingRequest;
      const url = isEdit
        ? `${BACKEND}/api/attendance/absence-request/${editingRequest.request_id}`
        : `${BACKEND}/api/attendance/absence-request`;

      const res = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (res.ok) {
        toast.success(isEdit ? "Demande modifiée ✓" : "Demande envoyée ✓");
        resetForm();
        loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur");
      }
    } catch { toast.error("Erreur de connexion"); }
    setSubmitting(false);
  };

  const deleteRequest = async (requestId) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette demande ?")) return;
    setDeleting(requestId);
    try {
      const res = await api(`/attendance/absence-request/${requestId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Demande supprimée");
        loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur");
      }
    } catch { toast.error("Erreur de connexion"); }
    setDeleting(null);
  };

  const reviewRequest = async (requestId, status) => {
    try {
      const res = await api(`/attendance/absence-request/${requestId}`, {
        method: "PUT",
        body: JSON.stringify({ status, reviewer_comment: reviewComment }),
      });
      if (res.ok) {
        toast.success(`Demande ${STATUS_CONFIG[status]?.label?.toLowerCase()}`);
        setReviewId(null);
        setReviewComment("");
        loadData();
      } else toast.error("Erreur");
    } catch { toast.error("Erreur de connexion"); }
  };

  const downloadFile = (requestId) => {
    const token = localStorage.getItem("token");
    window.open(`${BACKEND}/api/attendance/absence-request/${requestId}/file?token=${token}`, "_blank");
  };

  const formatDateFR = (dt) => {
    try { return new Date(dt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return dt; }
  };

  const canEditOrDelete = (r) => {
    const isOwner = r.user_id === user?.id;
    return isOwner && (r.status === "pending" || r.status === "complement_needed");
  };

  if (loading) {
    return (<WorkspaceLayout><div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></WorkspaceLayout>);
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">

        {/* ═══ Header ═══ */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 700, display: "flex", alignItems: "center", gap: 12, color: "var(--foreground)" }}>
              <span style={{ background: "linear-gradient(135deg, #EF4444, #F87171)", padding: 10, borderRadius: 12, display: "flex" }}>
                <FileX className="h-6 w-6" style={{ color: "white" }} />
              </span>
              Demandes d'absence
            </h1>
            <p style={{ marginTop: 4, color: "var(--muted-foreground)", fontSize: 14 }}>
              {isCandidate ? "Soumettez et suivez vos demandes d'absence." : "Gérez les demandes d'absence des candidats."}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {canManage && (
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                style={{ borderRadius: 10, border: "1px solid var(--border)", padding: "8px 14px", fontSize: 13, background: "var(--background)" }}>
                <option value="">Tous les statuts</option>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            )}
            {isCandidate && (
              <Button onClick={openNewForm}
                style={{ gap: 6, borderRadius: 20, background: "linear-gradient(135deg, #EF4444, #F87171)", border: "none", color: "white" }}>
                <Plus className="h-4 w-4" /> Nouvelle demande
              </Button>
            )}
          </div>
        </div>

        {/* ═══ Create / Edit Form ═══ */}
        {showForm && (
          <Card style={{ marginBottom: 24, border: "1px solid var(--primary)", borderRadius: 16, overflow: "hidden" }}>
            <CardHeader style={{ background: editingRequest ? "linear-gradient(135deg, #6366f1, #818CF8)" : "linear-gradient(135deg, #EF4444, #F87171)", padding: "16px 24px" }}>
              <CardTitle style={{ color: "white", fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
                {editingRequest ? <><Pencil className="h-5 w-5" /> Modifier la demande</> : <><Send className="h-5 w-5" /> Nouvelle demande d'absence</>}
              </CardTitle>
            </CardHeader>
            <CardContent style={{ padding: 24 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "block" }}>Date de début *</label>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", padding: "10px 14px", fontSize: 14, background: "var(--background)" }} />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "block" }}>Date de fin *</label>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", padding: "10px 14px", fontSize: 14, background: "var(--background)" }} />
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "block" }}>Motif de l'absence *</label>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                  placeholder="Décrivez le motif de votre absence..."
                  style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", padding: "10px 14px", fontSize: 14, background: "var(--background)", resize: "vertical" }} />
              </div>
              <div style={{ marginTop: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "block" }}>
                  Pièce justificative {editingRequest ? "(remplacer)" : "(optionnel)"}
                </label>
                <div style={{
                  border: "2px dashed var(--border)", borderRadius: 12, padding: 20, textAlign: "center",
                  cursor: "pointer", background: "var(--muted)"
                }}
                onClick={() => document.getElementById("absence-file-input").click()}>
                  <input id="absence-file-input" type="file" style={{ display: "none" }}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={(e) => setFile(e.target.files[0])} />
                  {file ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                      <CheckCircle2 style={{ width: 16, height: 16, color: "#16a34a" }} />
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{file.name}</span>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }}
                        style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}>
                        <X style={{ width: 14, height: 14, color: "var(--muted-foreground)" }} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload style={{ width: 24, height: 24, color: "var(--muted-foreground)", margin: "0 auto 8px" }} />
                      <p style={{ fontSize: 13, color: "var(--muted-foreground)" }}>Cliquez pour uploader un justificatif</p>
                      <p style={{ fontSize: 11, color: "var(--muted-foreground)", opacity: 0.7 }}>PDF, JPG, PNG, DOC (max 5 Mo)</p>
                    </>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                <Button onClick={submitRequest} disabled={submitting}
                  style={{ gap: 6, borderRadius: 20, background: editingRequest ? "linear-gradient(135deg, #6366f1, #818CF8)" : "linear-gradient(135deg, #EF4444, #F87171)", border: "none", color: "white" }}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : editingRequest ? <Pencil className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                  {editingRequest ? "Sauvegarder" : "Envoyer la demande"}
                </Button>
                <Button variant="ghost" onClick={resetForm} style={{ borderRadius: 20 }}>Annuler</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ═══ Requests List ═══ */}
        {requests.length === 0 ? (
          <Card style={{ borderRadius: 20, border: "2px dashed var(--border)" }}>
            <CardContent style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "64px 24px" }}>
              <FileX className="h-12 w-12" style={{ color: "var(--muted-foreground)", opacity: 0.4 }} />
              <h3 style={{ marginTop: 16, fontWeight: 600, fontSize: 16, color: "var(--muted-foreground)" }}>Aucune demande d'absence</h3>
            </CardContent>
          </Card>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {requests.map((r) => {
              const sc = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
              const Icon = sc.icon;
              const isReviewing = reviewId === r.request_id;
              const editable = canEditOrDelete(r);
              return (
                <Card key={r.request_id} style={{
                  borderRadius: 16, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                  borderLeft: `4px solid ${sc.color}`, transition: "box-shadow 0.2s"
                }}>
                  <CardContent style={{ padding: 20 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        {canManage && (
                          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{r.user_name}
                            <span style={{ fontSize: 12, color: "var(--muted-foreground)", fontWeight: 400, marginLeft: 8 }}>{r.user_email}</span>
                          </div>
                        )}
                        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--foreground)", marginBottom: 6 }}>
                          <Clock style={{ width: 14, height: 14, color: "var(--muted-foreground)" }} />
                          <strong>{formatDateFR(r.start_date)}</strong> → <strong>{formatDateFR(r.end_date)}</strong>
                        </div>
                        <p style={{ fontSize: 13, color: "var(--muted-foreground)", lineHeight: 1.6 }}>{r.reason}</p>
                        {r.reviewer_comment && (
                          <div style={{
                            marginTop: 10, padding: "8px 14px", borderRadius: 10,
                            background: "var(--muted)", fontSize: 12, color: "var(--foreground)",
                            borderLeft: `3px solid ${sc.color}`
                          }}>
                            <strong>{r.reviewer_name || "Reviewer"} :</strong> {r.reviewer_comment}
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                        <Badge style={{
                          background: sc.bg, color: sc.color, border: `1px solid ${sc.color}30`,
                          borderRadius: 20, fontSize: 11, fontWeight: 600, gap: 4, display: "inline-flex", alignItems: "center", padding: "4px 12px"
                        }}>
                          <Icon style={{ width: 12, height: 12 }} /> {sc.label}
                        </Badge>
                        <span style={{ fontSize: 10, color: "var(--muted-foreground)" }}>
                          {formatDateFR(r.created_at)}
                        </span>
                        {r.file_name && (
                          <Button variant="outline" size="sm" onClick={() => downloadFile(r.request_id)}
                            style={{ gap: 4, borderRadius: 20, fontSize: 11 }}>
                            <Download style={{ width: 12, height: 12 }} /> {r.file_name}
                          </Button>
                        )}
                        {/* Edit / Delete for candidate */}
                        {editable && (
                          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                            <Button variant="outline" size="sm" onClick={() => openEditForm(r)}
                              style={{ gap: 4, borderRadius: 20, fontSize: 11 }}>
                              <Pencil style={{ width: 12, height: 12 }} /> Modifier
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => deleteRequest(r.request_id)}
                              disabled={deleting === r.request_id}
                              style={{ gap: 4, borderRadius: 20, fontSize: 11, color: "#dc2626", borderColor: "#dc262640" }}>
                              {deleting === r.request_id ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" /> : <Trash2 style={{ width: 12, height: 12 }} />}
                              Supprimer
                            </Button>
                          </div>
                        )}
                        {/* Admin can also delete */}
                        {canManage && (
                          <Button variant="outline" size="sm" onClick={() => deleteRequest(r.request_id)}
                            disabled={deleting === r.request_id}
                            style={{ gap: 4, borderRadius: 20, fontSize: 11, color: "#dc2626", borderColor: "#dc262640" }}>
                            {deleting === r.request_id ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" /> : <Trash2 style={{ width: 12, height: 12 }} />}
                            Supprimer
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Admin review actions */}
                    {canManage && (r.status === "pending" || r.status === "complement_needed") && (
                      <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
                        {isReviewing ? (
                          <div>
                            <textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)}
                              placeholder="Commentaire (optionnel)..." rows={2}
                              style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", padding: "8px 12px", fontSize: 13, background: "var(--background)", resize: "none", marginBottom: 10 }} />
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <Button size="sm" onClick={() => reviewRequest(r.request_id, "approved")}
                                style={{ gap: 4, borderRadius: 20, background: "#16a34a", border: "none", color: "white", fontSize: 12 }}>
                                <CheckCircle2 style={{ width: 12, height: 12 }} /> Approuver
                              </Button>
                              <Button size="sm" onClick={() => reviewRequest(r.request_id, "rejected")}
                                style={{ gap: 4, borderRadius: 20, background: "#dc2626", border: "none", color: "white", fontSize: 12 }}>
                                <XCircle style={{ width: 12, height: 12 }} /> Refuser
                              </Button>
                              <Button size="sm" onClick={() => reviewRequest(r.request_id, "complement_needed")}
                                style={{ gap: 4, borderRadius: 20, background: "#6366f1", border: "none", color: "white", fontSize: 12 }}>
                                <AlertCircle style={{ width: 12, height: 12 }} /> Demander complément
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => { setReviewId(null); setReviewComment(""); }}
                                style={{ borderRadius: 20, fontSize: 12 }}>Annuler</Button>
                            </div>
                          </div>
                        ) : (
                          <Button variant="outline" size="sm" onClick={() => setReviewId(r.request_id)}
                            style={{ gap: 4, borderRadius: 20, fontSize: 12 }}>
                            <Eye style={{ width: 12, height: 12 }} /> Examiner cette demande
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </WorkspaceLayout>
  );
}
